import type { Conversation, Message } from '@/types/domain';
import { tr } from '@/i18n/tr';
import { api } from './apiClient';
import { signalBadgesChanged } from './badgeSignal';

/**
 * Chat — live endpoints under /chat.
 *
 * Sending is REST, not a socket event: one write path,
 * and a dropped socket costs freshness instead of costing a message. The socket
 * (useChatSocket) only delivers what this service has already persisted.
 *
 * Wire ids are numeric; the rest of the app models ids as strings, so this
 * module is the seam that converts — same pattern as listingService.
 */

/**
 * Stand-in id for a peer whose account is gone. A real id can never collide
 * with it, and it keeps `peer.id` a plain string for every consumer.
 */
export const DELETED_PEER_ID = 'deleted';

interface ApiConversation {
  id: number;
  listingId: number | null;
  listingTitle: string | null;
  peer: {
    /** null once that account is deleted; the thread stays readable. */
    id: number | null;
    /** The literal sentinel 'deleted_user' when the peer is gone. */
    name: string;
    avatarUrl: string | null;
    isOnline: boolean;
  };
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  blockedByMe: boolean;
  reportPending: boolean;
}

interface ApiMessage {
  id: number;
  conversationId: number;
  senderId: number;
  content: string;
  /** Set on the message that opened a topic about a listing. */
  listingId: number | null;
  listingTitle: string | null;
  sentAt: string;
  readAt: string | null;
}

export function toConversation(row: ApiConversation): Conversation {
  return {
    id: String(row.id),
    listingId: row.listingId != null ? String(row.listingId) : null,
    listingTitle: row.listingTitle,
    peer: {
      // The backend sends a sentinel rather than Turkish copy, so the label is
      // resolved here where the rest of the app's wording lives.
      id: row.peer.id != null ? String(row.peer.id) : DELETED_PEER_ID,
      displayName: row.peer.id == null ? tr.chat.deletedUser : row.peer.name,
      avatarUrl: row.peer.avatarUrl ?? undefined,
      isOnline: row.peer.isOnline,
    },
    lastMessagePreview: row.lastMessagePreview,
    lastMessageAt: row.lastMessageAt,
    unreadCount: row.unreadCount,
    blockedByMe: row.blockedByMe,
    reportPending: row.reportPending,
  };
}

export function toMessage(row: ApiMessage): Message {
  return {
    id: String(row.id),
    conversationId: String(row.conversationId),
    senderId: String(row.senderId),
    content: row.content,
    listingId: row.listingId != null ? String(row.listingId) : null,
    listingTitle: row.listingTitle,
    sentAt: row.sentAt,
    readAt: row.readAt,
  };
}

/** Inbox, newest activity first. */
export async function getConversations(): Promise<Conversation[]> {
  const rows = await api<ApiConversation[]>('/chat/conversations');
  return rows.map(toConversation);
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  const row = await api<ApiConversation>(`/chat/conversations/${conversationId}`);
  return toConversation(row);
}

/**
 * A page of messages, oldest→newest. `before` is a message id: pass the oldest
 * one currently on screen to load the page above it.
 */
export async function getMessages(
  conversationId: string,
  options: { before?: string; limit?: number } = {}
): Promise<Message[]> {
  const params = new URLSearchParams();
  if (options.before) params.set('before', options.before);
  if (options.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  const rows = await api<ApiMessage[]>(
    `/chat/conversations/${conversationId}/messages${query ? `?${query}` : ''}`
  );
  return rows.map(toMessage);
}

/**
 * `listingId` tags this message as the one that opened a topic about a listing
 * — sent only for the FIRST message after arriving from that listing, so the
 * pair keeps one conversation instead of one thread per listing.
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  listingId?: string | null
): Promise<Message> {
  const row = await api<ApiMessage>(`/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: listingId ? { content, listingId: Number(listingId) } : { content },
  });
  return toMessage(row);
}

export async function blockConversation(conversationId: string): Promise<void> {
  await api<void>(`/chat/conversations/${conversationId}/block`, { method: 'POST' });
}

export async function unblockConversation(conversationId: string): Promise<void> {
  await api<void>(`/chat/conversations/${conversationId}/block`, { method: 'DELETE' });
}

export async function reportConversation(
  conversationId: string,
  messageIds: string[],
  description: string
): Promise<void> {
  await api(`/chat/conversations/${conversationId}/report`, {
    method: 'POST',
    body: { messageIds: messageIds.map(Number), description },
  });
  // The answered-report badge lives on the Bildir tab; a new report can change it.
  signalBadgesChanged();
}

export async function markConversationRead(conversationId: string): Promise<number> {
  const { readCount } = await api<{ readCount: number }>(
    `/chat/conversations/${conversationId}/read`,
    { method: 'PATCH' }
  );
  if (readCount > 0) signalBadgesChanged();
  return readCount;
}

export async function getUnreadCount(): Promise<number> {
  const { total } = await api<{ total: number }>('/chat/conversations/unread-count');
  return total;
}

/**
 * Start-or-resume. Idempotent server-side, so the caller does not need to know
 * whether a thread already exists — tapping "Mesaj Gönder" twice is harmless.
 * Exactly one of listingId / producerId.
 */
export async function startConversation(target: {
  listingId?: string;
  producerId?: string;
}): Promise<Conversation> {
  const row = await api<ApiConversation>('/chat/conversations', {
    method: 'POST',
    body: target.listingId
      ? { listingId: Number(target.listingId) }
      : { producerId: Number(target.producerId) },
  });
  return toConversation(row);
}

/**
 * Canned openers above the composer.
 * Static copy, no backend — a wording change is a one-line edit here.
 */
export const QUICK_REPLIES: string[] = [
  'Merhaba, ürününüz hâlâ mevcut mu?',
  'Fiyatta pazarlık payı var mıdır?',
  'Kargo/nakliye imkânı var mı?',
  'Numune gönderebilir misiniz?',
];
